import request from './request'
import { getMockCouponDetailResponse, shouldUseCouponMock } from './couponCenterMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface CouponDetailParams {
  coupon_id: string
}

export interface CouponDetailData {
  id?: number
  coupon_id: string
  name: string
  code: string
  picture_url?: string
  discount_type?: string
  discount_type_text?: string
  discount_value?: number
  discount_text?: string
  min_spend_required?: number
  min_spend_amount?: number
  min_spend_text?: string
  start_mode?: string
  start_time?: string
  end_mode?: string
  end_time?: string
  validity_period_text?: string
  eligible_services?: string[]
  eligible_services_text?: string
  usage_limit_type?: string
  usage_limit_text?: string
  eligible_regions?: string
  eligible_regions_text?: string
  total_redemptions_unlimited?: number
  total_redemptions_limit?: number
  total_redemptions_used?: number
  redemptions_text?: string
  visibility?: string
  visibility_text?: string
  visible_to?: string
  specified_users_file?: string
  availability?: string
  availability_text?: string
  description?: string
  [key: string]: unknown
}

export type CouponDetailVisibilityTone = 'visible' | 'hidden'
export type CouponDetailAvailabilityTone = 'active' | 'inactive'

export interface CouponDetailView {
  couponId: string
  name: string
  code: string
  pictureUrl: string
  discountType: string
  discountAmount: string
  minSpend: string
  validityPeriod: string
  eligibleServices: string
  usageLimit: string
  eligibleRegions: string
  totalRedemptions: string
  visibility: string
  visibilityTone: CouponDetailVisibilityTone
  availability: string
  availabilityTone: CouponDetailAvailabilityTone
  description: string
}

function normalizeCouponAssetPath(path?: string): string {
  return (path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
}

/**
 * 与技师详情 `getAvatarUrl` 一致：http 直用，否则 VITE_BASE_URL + path。
 * 优惠券 upload 存的是 `uploads/admin/...`（无前导 /），公网需 `/storage/` 前缀。
 */
export function resolveCouponPictureUrl(path?: string): string {
  const raw = normalizeCouponAssetPath(path)
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) {
    return raw
  }

  let pathname = raw.startsWith('/') ? raw : `/${raw}`
  if (!pathname.startsWith('/storage/') && pathname.startsWith('/uploads/')) {
    pathname = `/storage${pathname}`
  }

  const base = (import.meta.env.VITE_BASE_URL || '').replace(/\/$/, '')
  return `${base}${pathname}`
}

function readCouponPictureRaw(data: CouponDetailData): string {
  const keys = [
    'picture_url',
    'picture',
    'image_url',
    'image',
    'photo_url',
    'photo',
    'picture_path',
    'image_path',
  ] as const

  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim()) return normalizeCouponAssetPath(value)
  }
  return ''
}

function resolveVisibilityTone(visibility?: string): CouponDetailVisibilityTone {
  const value = (visibility ?? '').toLowerCase()
  if (value === 'visible' || value === '1') return 'visible'
  if (value === 'hidden' || value === '0') return 'hidden'
  return value.includes('visible') ? 'visible' : 'hidden'
}

function resolveAvailabilityTone(availability?: string): CouponDetailAvailabilityTone {
  const value = (availability ?? '').toLowerCase()
  if (value === 'active' || value === '1') return 'active'
  if (value === 'inactive' || value === '0') return 'inactive'
  return value.includes('active') ? 'active' : 'inactive'
}

function resolveUsageLimitText(data: CouponDetailData): string {
  const type = (data.usage_limit_type ?? '').toLowerCase()
  const text = data.usage_limit_text?.trim() || ''
  if (
    type.includes('one_per_account') ||
    /account or address/i.test(text) ||
    /one-time use per account/i.test(text)
  ) {
    return 'One-time use per account'
  }
  return text
}

function resolveDiscountAmount(data: CouponDetailData): string {
  if (data.discount_text?.trim()) {
    const match = data.discount_text.match(/(\$[\d.,]+|\d+(?:\.\d+)?%)/)
    if (match?.[1]) return match[1]
  }
  if (typeof data.discount_value === 'number') {
    const type = (data.discount_type ?? '').toLowerCase()
    if (type.includes('percent')) return `${data.discount_value}%`
    return `$${data.discount_value.toFixed(2)}`
  }
  return data.discount_text?.trim() ?? ''
}

function resolveTotalRedemptions(data: CouponDetailData): string {
  if (data.redemptions_text?.trim()) return data.redemptions_text.trim()
  if (data.total_redemptions_unlimited === 1) return 'Unlimited'
  const used = data.total_redemptions_used
  const limit = data.total_redemptions_limit
  if (typeof used === 'number' && typeof limit === 'number') return `${used}/${limit}`
  return ''
}

export function mapCouponDetailToView(data: CouponDetailData, fallbackPicture = ''): CouponDetailView {
  const visibilityRaw = data.visibility ?? ''
  const availabilityRaw = data.availability ?? ''

  return {
    couponId: data.coupon_id,
    name: data.name ?? '',
    code: data.code ?? '',
    pictureUrl: resolveCouponPictureUrl(readCouponPictureRaw(data)) || fallbackPicture,
    discountType: data.discount_type_text?.trim() || data.discount_type?.trim() || '',
    discountAmount: resolveDiscountAmount(data),
    minSpend: data.min_spend_text?.trim() || 'Not required',
    validityPeriod: data.validity_period_text?.trim() || '',
    eligibleServices: data.eligible_services_text?.trim() || '',
    usageLimit: resolveUsageLimitText(data),
    eligibleRegions: data.eligible_regions_text?.trim() || data.eligible_regions?.trim() || '',
    totalRedemptions: resolveTotalRedemptions(data),
    visibility: data.visibility_text?.trim() || visibilityRaw,
    visibilityTone: resolveVisibilityTone(visibilityRaw),
    availability: data.availability_text?.trim() || availabilityRaw,
    availabilityTone: resolveAvailabilityTone(availabilityRaw),
    description: data.description?.trim() || '',
  }
}

/** POST `/api/admin/coupon/detail` */
export async function postCouponDetail(
  params: CouponDetailParams,
): Promise<ApiResponse<CouponDetailData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponDetailResponse(params.coupon_id) as ApiResponse<CouponDetailData>
  }
  return request.post<ApiResponse<CouponDetailData>>('/api/admin/coupon/detail', params)
}
